/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useRef, useState } from 'react';
import { HWKeyEvent, ScrollView, StyleSheet, TVEventHandler, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useIsFocused } from '@react-navigation/native';

import { ALERTS_NON_PLATED_ICON_MAP } from '@assets';
import { alertDetailsPageTestIDs, Theme } from '@constants';
import { helpers } from '@helpers';
import { useGetExpireTimeText } from '@hooks';

import { TWCAlertIcon } from '../../primitives/TWCAlertIcon/TWCAlertIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'currentWeatherConditionsPage.alertDetails.';
const testIDPath = alertDetailsPageTestIDs;

const SCROLL_HEIGHT = Theme.px(680);
const SCROLL_INCREMENT = Theme.px(120);

type AlertDetailsViewProps = {
  alertDetail: {
    eventDescription: keyof typeof ALERTS_NON_PLATED_ICON_MAP;
    expireTimeUTC: number;
    expireTimeLocalTimeZone: string;
    texts: { description: string }[];
  };
  index?: number;
  isFocused?: boolean;
};

export const AlertDetailsView: FC<AlertDetailsViewProps> = ({ alertDetail, index, isFocused = false }) => {
  const { eventDescription, expireTimeUTC, expireTimeLocalTimeZone, texts } = alertDetail;

  const isTodayTomorrowOrLater = helpers.TimeUtils.isTodayTomorrowOrLater(expireTimeUTC);

  const expireTimeText = useGetExpireTimeText({
    expireTimeUTC,
    issueTimeLocalTimeZone: expireTimeLocalTimeZone,
    isTodayTomorrowOrLater,
    tPath,
  });

  const scrollViewReference = useRef<ScrollView>(null);

  const [contentHeight, setContentHeight] = useState(SCROLL_HEIGHT);
  const [scrollOffset, setScrollOffset] = useState(0);

  const scrollHandler = (keyEvent: any) => {
    if (isFocused) {
      const key = keyEvent.key;

      switch (key) {
        case 'ArrowUp': {
          const newOffset = Math.max(scrollOffset - SCROLL_INCREMENT, 0);

          if (newOffset !== scrollOffset) {
            setScrollOffset(newOffset);
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
        case 'ArrowDown': {
          const newOffset = Math.min(scrollOffset + SCROLL_INCREMENT, contentHeight - SCROLL_HEIGHT);

          if (newOffset !== scrollOffset) {
            setScrollOffset(newOffset);
            keyEvent.preventDefault && keyEvent.preventDefault();
          }
          break;
        }
      }
    }
  };

  const isPageFocused = useIsFocused();

  useEffect(() => {
    const tvEventHandler = new TVEventHandler();

    // Only scroll if currently on top of nav stack (some modals can show on top)
    if (isPageFocused) {
      if (helpers.isWeb) {
        document.addEventListener('keydown', scrollHandler, true);
      } else {
        const handleEvent = (cmp: React.Component<unknown>, event: HWKeyEvent) => {
          if (
            isFocused &&
            event &&
            event.eventType &&
            (event.eventType === 'down' || event.eventType === 'up') &&
            event.eventKeyAction === 0
          ) {
            scrollHandler({
              key: event.eventType === 'down' ? 'ArrowDown' : 'ArrowUp',
            });
          }
        };

        tvEventHandler.enable(undefined, handleEvent);
      }
      scrollViewReference.current?.scrollTo({
        x: 0,
        y: Math.min(scrollOffset, contentHeight),
        animated: true,
      });
    }

    return () => {
      if (helpers.isWeb) {
        document.removeEventListener('keydown', scrollHandler, true);
      } else {
        tvEventHandler?.disable();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollOffset, contentHeight, isFocused, isPageFocused]);

  return (
    <View
      key={`alert_detail_index_${index}`}
      accessible={false}
      importantForAccessibility={'no-hide-descendants'}
      style={styles.alertModalContent}
    >
      <View style={styles.alertModalHeader}>
        <View style={styles.alertModalHeaderIconWrapper}>
          <TWCAlertIcon eventDescription={eventDescription} height={84} width={84} />
        </View>
        <View style={styles.alertModalHeaderTextWrapper}>
          <TWCText importantForAccessibility={'no'} testID={testIDPath.eventDescription} variant={'regularText48'}>
            {eventDescription}
          </TWCText>
          <TWCText
            importantForAccessibility={'no'}
            testID={testIDPath.expireTimeText}
            variant={'regularText36Transparent'}
          >
            {expireTimeText}
          </TWCText>
        </View>
      </View>
      <View style={styles.alertModalBody}>
        <ScrollView
          accessible={false}
          indicatorStyle={'white'}
          persistentScrollbar
          ref={scrollViewReference}
          testID={`alert_modal_content_index_${index}`}
          onContentSizeChange={(_width, height) => {
            if (scrollOffset > height - Theme.px(680)) {
              setScrollOffset(height - Theme.px(680));
            }
            setContentHeight(height);
          }}
        >
          <TWCText
            importantForAccessibility={'no'}
            style={styles.alertModalDetailText}
            testID={testIDPath.detailText}
            variant={'regularText34'}
          >
            {`${texts[0]?.description?.replace(/&&/g, '')}\n\n\n\n`}
          </TWCText>
        </ScrollView>
        <LinearGradient
          colors={[Theme.gradients.alertModal.start, Theme.gradients.alertModal.end]}
          pointerEvents={'none'}
          style={styles.alertModalGradient}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  alertModalBody: {
    backgroundColor: Theme.blackPearlBlue,
    borderBottomLeftRadius: Theme.px(24),
    borderBottomRightRadius: Theme.px(24),
    height: Theme.px(732),
    paddingHorizontal: Theme.px(60),
    paddingTop: Theme.px(45),
    width: Theme.px(1160),
  },
  alertModalContent: {
    alignItems: 'center',
    flex: 1,
    height: Theme.px(912),
    paddingTop: Theme.px(60),
    width: Theme.px(1920),
  },
  alertModalDetailText: {
    paddingRight: Theme.px(25),
  },
  alertModalGradient: {
    borderBottomLeftRadius: Theme.px(24),
    borderBottomRightRadius: Theme.px(24),
    bottom: 0,
    height: Theme.px(218),
    left: 0,
    position: 'absolute',
    width: Theme.px(1160),
  },
  alertModalHeader: {
    backgroundColor: Theme.alertRed,
    borderTopLeftRadius: Theme.px(24),
    borderTopRightRadius: Theme.px(24),
    flexDirection: 'row',
    height: Theme.px(180),
    width: Theme.px(1160),
  },
  alertModalHeaderIconWrapper: {
    paddingLeft: Theme.px(80),
    paddingTop: Theme.px(42),
  },
  alertModalHeaderTextWrapper: {
    paddingLeft: Theme.px(10),
    paddingTop: Theme.px(33),
  },
});
